import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { finishedProductsAPI, recipeCompositionsAPI, materialsAPI } from '@/utils/api';
import { Plus, Trash2, ArrowLeft, Pencil, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ProductRecipe = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [compositions, setCompositions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [formData, setFormData] = useState({ material_id: '', quantity_needed: '' });

  useEffect(() => {
    loadData();
  }, [productId]);

  const loadData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const [prodRes, compsRes, matsRes] = await Promise.all([
        finishedProductsAPI.getOne(productId),
        recipeCompositionsAPI.getByProduct(productId),
        materialsAPI.getAll(user.id || 'demo-user')
      ]);
      setProduct(prodRes.data);
      setCompositions(compsRes.data);
      setMaterials(matsRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await recipeCompositionsAPI.create({
        finished_product_id: productId,
        material_id: formData.material_id,
        quantity_needed: parseFloat(formData.quantity_needed)
      });
      toast.success('Ingrédient ajouté');
      setDialogOpen(false);
      setFormData({ material_id: '', quantity_needed: '' });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Retirer cet ingrédient ?')) return;
    try {
      await recipeCompositionsAPI.delete(id);
      toast.success('Ingrédient retiré');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleEditQty = async (compositionId) => {
    const qty = parseFloat(editQty);
    if (isNaN(qty) || qty <= 0) { toast.error('Quantité invalide'); return; }
    try {
      await recipeCompositionsAPI.update(compositionId, { quantity_needed: qty });
      toast.success('Quantité modifiée');
      setEditingId(null);
      setEditQty('');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  // Séparer les ingrédients des contenants/packaging selon material.category
  const CONTAINER_CATS = ['Contenant', 'Packaging'];
  const materialCatById = Object.fromEntries(materials.map(m => [m.id, m.category]));
  const ingredientComps = compositions.filter(c => !CONTAINER_CATS.includes(materialCatById[c.material_id]));
  const containerComps = compositions.filter(c => CONTAINER_CATS.includes(materialCatById[c.material_id]));
  const totalCost = compositions.reduce((sum, c) => sum + c.line_cost, 0);
  const ingredientsCost = ingredientComps.reduce((sum, c) => sum + c.line_cost, 0);
  const containersCost = containerComps.reduce((sum, c) => sum + c.line_cost, 0);

  const renderCompositionRow = (c) => (
    <div key={c.id} className="flex justify-between items-center p-3 border border-border rounded-md">
      <div>
        <p className="font-medium" data-testid={`composition-name-${c.id}`}>{c.material_name}</p>
        {editingId === c.id ? (
          <div className="flex items-center gap-2 mt-1">
            <Input type="number" step="0.01" className="w-24 h-7 text-sm" value={editQty} onChange={(e) => setEditQty(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleEditQty(c.id)} autoFocus />
            <span className="text-xs text-muted-foreground">gr</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleEditQty(c.id)}>OK</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingId(null)}>Annuler</Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => { setEditingId(c.id); setEditQty(c.quantity_needed.toString()); }}>
            Quantité: {c.quantity_needed} gr <Pencil size={12} className="inline ml-1" />
          </p>
        )}
      </div>
      <div className="flex items-center gap-3">
        <p className="font-semibold">{c.line_cost.toFixed(2)}€</p>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(c.id)}>
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/production/products')} className="gap-2">
          <ArrowLeft size={18} /> Retour aux produits
        </Button>
        {compositions.length > 0 && (
          <Button variant="outline" className="gap-2" data-testid="btn-export-recipe-pdf"
            onClick={() => window.open(`${API_BASE}/api/finished-products/${productId}/recipe-pdf`, '_blank')}>
            <FileDown size={18} /> Imprimer PDF
          </Button>
        )}
      </div>

      <div className="mb-8">
        <h1 className="text-4xl font-semibold mb-2">Recette: {product?.name}</h1>
        <p className="text-muted-foreground">Composition et coûts</p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Ingrédients cosmétiques</CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="btn-add-composition"><Plus size={16} className="mr-2" /> Ajouter</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter une matière (ingrédient ou contenant)</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAdd} className="space-y-4 mt-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Matière Première</label>
                    <select 
                      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2" 
                      value={formData.material_id} 
                      onChange={(e) => setFormData({...formData, material_id: e.target.value})}
                      required
                      data-testid="select-material"
                    >
                      <option value="">-- Sélectionner --</option>
                      {materials.map(m => (
                        <option key={m.id} value={m.id}>{m.name} [{m.category}] ({m.unit_cost.toFixed(4)}€/{m.unit})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Quantité Nécessaire</label>
                    <Input type="number" step="0.01" value={formData.quantity_needed} onChange={(e) => setFormData({...formData, quantity_needed: e.target.value})} required placeholder="Ex: 50" />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
                    <Button type="submit">Ajouter</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {ingredientComps.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucun ingrédient. Ajoutez la composition.</p>
          ) : (
            <div className="space-y-2">
              {ingredientComps.map(renderCompositionRow)}
              <div className="flex justify-between items-center p-3 bg-muted/50 rounded-md mt-2">
                <p className="text-sm font-medium text-muted-foreground">Sous-total ingrédients</p>
                <p className="font-semibold">{ingredientsCost.toFixed(2)}€</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Conditionnement &amp; Contenants</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Pots, étiquettes, packaging — n'entrent pas dans le % de la formule cosmétique</p>
        </CardHeader>
        <CardContent>
          {containerComps.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">Aucun contenant. Utilisez "Ajouter" ci-dessus puis sélectionnez une matière de catégorie Contenant ou Packaging.</p>
          ) : (
            <div className="space-y-2">
              {containerComps.map(renderCompositionRow)}
              <div className="flex justify-between items-center p-3 bg-muted/50 rounded-md mt-2">
                <p className="text-sm font-medium text-muted-foreground">Sous-total conditionnement</p>
                <p className="font-semibold">{containersCost.toFixed(2)}€</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {compositions.length > 0 && (
        <div className="flex justify-between items-center p-4 bg-primary/5 rounded-md border-2 border-primary/20 mb-6">
          <p className="font-semibold text-lg">Coût de Revient Total</p>
          <p className="text-2xl font-bold text-primary">{totalCost.toFixed(2)}€</p>
        </div>
      )}

      {product && (
        <Card>
          <CardHeader>
            <CardTitle>Rentabilité</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">Marge Brute Public</p>
                <p className="text-xl font-bold text-green-600">+{product.gross_margin_public.toFixed(2)}€</p>
                <p className="text-xs text-muted-foreground mt-1">Coef: {product.coefficient_public.toFixed(2)}x</p>
              </div>
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">Marge Brute Pro</p>
                <p className="text-xl font-bold text-green-600">+{product.gross_margin_pro.toFixed(2)}€</p>
                <p className="text-xs text-muted-foreground mt-1">Coef: {product.coefficient_pro.toFixed(2)}x</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductRecipe;
